import { httpCoreService } from "../services/HttpCoreService";
import type { ApiResponse } from "../types/auth.type";
import type {
  DashboardOverview,
  SponsorDataManagement,
  SponsorEnrollmentAnalytics,
  SponsorFinancialOverview,
  SponsorMilestones,
  SponsorMonitoringSummary,
  SponsorStudyInfo,
  SponsorSaes,
  SponsorAlerts,
  IwrsDashboardData,
} from "../types/dashboard.types";

function assertSuccess<T>(
  response: ApiResponse<T>,
  errorMsg: string,
): asserts response is ApiResponse<T> & { data: T } {
  const isSuccess = response.statusCode >= 200 && response.statusCode < 300;
  if (!isSuccess) {
    throw Object.assign(
      new Error(response.error ?? response.message ?? errorMsg),
      {
        status: response.statusCode,
      },
    );
  }
  if (!response.data) {
    throw new Error(`${errorMsg}: No data returned from server.`);
  }
}

export interface IDashboardRepository {
  getOverview(feasibleSitesOnly?: boolean): Promise<DashboardOverview>;
  getSponsorStudyInfo(
    studyId?: string,
    feasibleSitesOnly?: boolean,
  ): Promise<SponsorStudyInfo>;
  getSponsorEnrollmentAnalytics(
    studyId?: string,
    feasibleSitesOnly?: boolean,
  ): Promise<SponsorEnrollmentAnalytics>;
  getSponsorMonitoringSummary(
    studyId?: string,
    feasibleSitesOnly?: boolean,
  ): Promise<SponsorMonitoringSummary>;
  getSponsorDataManagement(studyId?: string): Promise<SponsorDataManagement>;
  getSponsorMilestones(
    studyId?: string,
    feasibleSitesOnly?: boolean,
  ): Promise<SponsorMilestones>;
  getSponsorFinancialOverview(
    studyId?: string,
    feasibleSitesOnly?: boolean,
  ): Promise<SponsorFinancialOverview>;
  getSponsorSaes(studyId?: string): Promise<SponsorSaes>;
  getSponsorAlerts(
    studyId?: string,
    feasibleSitesOnly?: boolean,
  ): Promise<SponsorAlerts>;
  getSponsorIwrsData(studyId?: string): Promise<IwrsDashboardData>;
}

export class DashboardRepository implements IDashboardRepository {
  async getOverview(feasibleSitesOnly = false): Promise<DashboardOverview> {
    const query = feasibleSitesOnly ? "?feasibleSitesOnly=true" : "";
    const response = await httpCoreService.get<DashboardOverview>(
      `/dashboard/overview${query}`,
    );
    assertSuccess(response, "Failed to fetch dashboard overview");
    const data = response.data;
    // Guard partial API payloads so Tenant Dashboard never crashes on .map
    return {
      ...data,
      studyStatusDistribution: data.studyStatusDistribution ?? [],
      recentActivity: data.recentActivity ?? [],
      studyList: data.studyList ?? [],
      upcomingVisits: data.upcomingVisits ?? [],
      myTasks: data.myTasks ?? [],
    };
  }

  async getSponsorStudyInfo(
    studyId?: string,
    feasibleSitesOnly = false,
  ): Promise<SponsorStudyInfo> {
    const params = new URLSearchParams();
    if (studyId) {
      params.set("studyId", studyId);
    }
    if (feasibleSitesOnly) {
      params.set("feasibleSitesOnly", "true");
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await httpCoreService.get<SponsorStudyInfo>(
      `/dashboard/sponsor/study-info${query}`,
    );
    assertSuccess(response, "Failed to fetch sponsor study info");
    return response.data;
  }

  async getSponsorEnrollmentAnalytics(
    studyId?: string,
    feasibleSitesOnly = false,
  ): Promise<SponsorEnrollmentAnalytics> {
    const params = new URLSearchParams();
    if (studyId) {
      params.set("studyId", studyId);
    }
    if (feasibleSitesOnly) {
      params.set("feasibleSitesOnly", "true");
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await httpCoreService.get<SponsorEnrollmentAnalytics>(
      `/dashboard/sponsor/enrollment-analytics${query}`,
    );
    assertSuccess(response, "Failed to fetch sponsor enrollment analytics");
    return response.data;
  }

  async getSponsorMonitoringSummary(
    studyId?: string,
    feasibleSitesOnly = false,
  ): Promise<SponsorMonitoringSummary> {
    const params = new URLSearchParams();
    if (studyId) {
      params.set("studyId", studyId);
    }
    if (feasibleSitesOnly) {
      params.set("feasibleSitesOnly", "true");
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await httpCoreService.get<SponsorMonitoringSummary>(
      `/dashboard/sponsor/monitoring-summary${query}`,
    );
    assertSuccess(response, "Failed to fetch sponsor monitoring summary");
    return response.data;
  }

  async getSponsorDataManagement(
    studyId?: string,
  ): Promise<SponsorDataManagement> {
    const params = new URLSearchParams();
    if (studyId) {
      params.set("studyId", studyId);
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await httpCoreService.get<SponsorDataManagement>(
      `/dashboard/sponsor/data-management${query}`,
    );
    assertSuccess(response, "Failed to fetch sponsor data management");
    return response.data;
  }

  async getSponsorMilestones(
    studyId?: string,
    feasibleSitesOnly = false,
  ): Promise<SponsorMilestones> {
    const params = new URLSearchParams();
    if (studyId) {
      params.set("studyId", studyId);
    }
    if (feasibleSitesOnly) {
      params.set("feasibleSitesOnly", "true");
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await httpCoreService.get<SponsorMilestones>(
      `/dashboard/sponsor/milestones${query}`,
    );
    assertSuccess(response, "Failed to fetch sponsor milestones");
    return response.data;
  }

  async getSponsorFinancialOverview(
    studyId?: string,
    feasibleSitesOnly = false,
  ): Promise<SponsorFinancialOverview> {
    const params = new URLSearchParams();
    if (studyId) {
      params.set("studyId", studyId);
    }
    if (feasibleSitesOnly) {
      params.set("feasibleSitesOnly", "true");
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await httpCoreService.get<SponsorFinancialOverview>(
      `/dashboard/sponsor/financial-overview${query}`,
    );
    assertSuccess(response, "Failed to fetch sponsor financial overview");
    return response.data;
  }

  async getSponsorSaes(studyId?: string): Promise<SponsorSaes> {
    const query = studyId ? `?studyId=${encodeURIComponent(studyId)}` : "";
    const response = await httpCoreService.get<SponsorSaes>(
      `/dashboard/sponsor/saes${query}`,
    );
    assertSuccess(response, "Failed to fetch sponsor SAEs");
    return response.data;
  }

  async getSponsorAlerts(
    studyId?: string,
    feasibleSitesOnly = false,
  ): Promise<SponsorAlerts> {
    const params = new URLSearchParams();
    if (studyId) {
      params.set("studyId", studyId);
    }
    if (feasibleSitesOnly) {
      params.set("feasibleSitesOnly", "true");
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await httpCoreService.get<SponsorAlerts>(
      `/dashboard/sponsor/alerts${query}`,
    );
    assertSuccess(response, "Failed to fetch sponsor alerts");
    return response.data;
  }

  async getSponsorIwrsData(studyId?: string): Promise<IwrsDashboardData> {
    const query = studyId ? `?studyId=${encodeURIComponent(studyId)}` : "";
    const response = await httpCoreService.get<IwrsDashboardData>(
      `/dashboard/sponsor/iwrs-data${query}`,
    );
    assertSuccess(response, "Failed to fetch IWRS dashboard data");
    return response.data;
  }
}
